import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CalendarDays, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { title: "", date: "", time: "", location: "", material: "", guide_id: "", program_id: "", status: "upcoming" };

export default function AdminManasik() {
  const [items, setItems] = useState(null);
  const [guides, setGuides] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [attendanceEvent, setAttendanceEvent] = useState(null);
  const [pilgrims, setPilgrims] = useState([]);

  const load = () => axios.get(`${API}/admin/manasik`).then(({ data }) => setItems(data)).catch(() => setItems([]));

  useEffect(() => {
    load();
    axios.get(`${API}/admin/pembimbing`).then(({ data }) => setGuides(data)).catch(() => {});
    axios.get(`${API}/admin/program`).then(({ data }) => setPrograms(data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...e, date: e.date?.slice(0, 10) }); setModalOpen(true); };
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    try {
      if (editing) await axios.put(`${API}/admin/manasik/${editing.id}`, form);
      else await axios.post(`${API}/admin/manasik`, form);
      toast.success("Jadwal manasik berhasil disimpan");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/manasik/${deleteId}`);
      toast.success("Jadwal berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openAttendance = async (event) => {
    setAttendanceEvent(event);
    const [jamaahRes, attendanceRes] = await Promise.all([
      axios.get(`${API}/admin/jamaah`, { params: event.program_id ? { program_id: event.program_id } : {} }),
      axios.get(`${API}/admin/manasik/${event.id}/attendance`),
    ]);
    const attMap = Object.fromEntries(attendanceRes.data.map((a) => [a.user_id, a.status]));
    setPilgrims(jamaahRes.data.map((j) => ({ ...j, attendance: attMap[j.user_id] || "" })));
  };

  const setAttendance = async (userId, status) => {
    await axios.post(`${API}/admin/manasik/${attendanceEvent.id}/attendance`, { user_id: userId, status });
    setPilgrims((prev) => prev.map((p) => (p.user_id === userId ? { ...p, attendance: status } : p)));
  };

  return (
    <div data-testid="admin-manasik-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Jadwal Manasik</h1>
          <p className="text-sm text-[#64748B]">Kelola jadwal pembinaan dan kehadiran jamaah.</p>
        </div>
        <Button data-testid="admin-manasik-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Jadwal</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Belum ada jadwal manasik" description="Tambahkan jadwal manasik pertama Anda." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-manasik-table">
            <TableHeader><TableRow><TableHead>Judul</TableHead><TableHead>Tanggal</TableHead><TableHead>Lokasi</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id} data-testid={`admin-manasik-row-${e.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{e.title}</TableCell>
                  <TableCell className="text-[#64748B]">{new Date(e.date).toLocaleDateString("id-ID")} &middot; {e.time}</TableCell>
                  <TableCell className="text-[#64748B]">{e.location}</TableCell>
                  <TableCell><StatusBadge status={e.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`admin-manasik-attendance-${e.id}`} onClick={() => openAttendance(e)}><ClipboardCheck className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-manasik-edit-${e.id}`} onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-manasik-delete-${e.id}`} onClick={() => setDeleteId(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="admin-manasik-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Jadwal" : "Tambah Jadwal"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul</Label><Input data-testid="manasik-form-title" value={form.title} onChange={(e) => update("title", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input data-testid="manasik-form-date" type="date" value={form.date} onChange={(e) => update("date", e.target.value)} /></div>
              <div><Label>Waktu</Label><Input data-testid="manasik-form-time" placeholder="09:00 - 12:00" value={form.time} onChange={(e) => update("time", e.target.value)} /></div>
            </div>
            <div><Label>Lokasi</Label><Input data-testid="manasik-form-location" value={form.location} onChange={(e) => update("location", e.target.value)} /></div>
            <div><Label>Materi</Label><Textarea data-testid="manasik-form-material" value={form.material} onChange={(e) => update("material", e.target.value)} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pembimbing</Label>
                <Select value={form.guide_id} onValueChange={(v) => update("guide_id", v)}>
                  <SelectTrigger data-testid="manasik-form-guide"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{guides.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Program</Label>
                <Select value={form.program_id} onValueChange={(v) => update("program_id", v)}>
                  <SelectTrigger data-testid="manasik-form-program"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger data-testid="manasik-form-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="upcoming">Mendatang</SelectItem><SelectItem value="today">Hari Ini</SelectItem><SelectItem value="completed">Selesai</SelectItem><SelectItem value="cancelled">Dibatalkan</SelectItem></SelectContent>
              </Select>
            </div>
            <Button data-testid="admin-manasik-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan Jadwal</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!attendanceEvent} onOpenChange={(v) => !v && setAttendanceEvent(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="admin-attendance-modal">
          <DialogHeader><DialogTitle>Kehadiran: {attendanceEvent?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {pilgrims.length === 0 ? <p className="text-sm text-[#64748B]">Belum ada jamaah pada program ini.</p> : pilgrims.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg bg-[#F5F9F7] px-3 py-2" data-testid={`attendance-row-${p.user_id}`}>
                <span className="text-sm font-medium text-[#1F2937]">{p.name}</span>
                <Select value={p.attendance} onValueChange={(v) => setAttendance(p.user_id, v)}>
                  <SelectTrigger data-testid={`attendance-select-${p.user_id}`} className="h-9 w-36"><SelectValue placeholder="Set status" /></SelectTrigger>
                  <SelectContent><SelectItem value="hadir">Hadir</SelectItem><SelectItem value="tidak_hadir">Tidak Hadir</SelectItem><SelectItem value="izin">Izin</SelectItem></SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Jadwal?" onConfirm={handleDelete} testId="admin-manasik-delete-confirm" />
    </div>
  );
}
